/**
 * VideoResultCard + VideoPlayerModal — the rich finished-video experience, restored
 * from the old ProjectDetailPage so the agent chat (and manual mode) get the
 * branded player back: captions on/off toggle (swaps the clean ⇄ burned-in
 * renditions), Download (forces a real save-to-disk), and Share (copy link + X /
 * WhatsApp / Telegram / Facebook / Email).
 *
 * The chat shows a clickable VideoResultCard (thumbnail); clicking opens the modal.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import {
  Play, X, Download, Link2, Facebook, Twitter, Check, Loader2, Share2, Send, MessageCircle, Mail, ArrowDown,
  ThumbsUp, ThumbsDown,
} from 'lucide-react';
import { cn } from '@/v2/utils';
import { ResultVideoPlayer, fmt } from '@/v2/components/ResultVideoPlayer';
import { SocialPublish } from '@/v2/components/SocialPublish';
import type { V2VideoPlan } from '@/api/v2Service';
import { API_CONFIG } from '@/api/config';
import { trackVideoDownloaded, trackVideoShared } from '@/services/analyticsService';
import { trackAmpVideoRated } from '@/services/amplitudeService';
import { getVideoRating, setVideoRating, videoRatingKey } from '@/v2/videoRating';

/** Rating confirmation beat: how long the chosen thumb stays lit, then the collapse. */
export const RATE_HOLD_MS = 900;
export const RATE_COLLAPSE_MS = 300;

/** Video result rendered inline in the chat — click opens the player. */
export function VideoResultCard({
  title,
  videoUrl,
  poster,
  durationSec,
  onOpen,
}: {
  title: string;
  videoUrl: string;
  /** Thumbnail — shown as the cover so the card isn't a black frame. */
  poster?: string;
  durationSec?: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation('v2');
  return (
    <div className="animate-fade-in">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary mb-2">
        {t('videoPlayer.videoLabel', { title })}
      </p>
      <button
        onClick={onOpen}
        className="group relative w-full aspect-video rounded-xl overflow-hidden bg-black border border-glass-border"
      >
        <video src={videoUrl} poster={poster} muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-14 h-14 rounded-full bg-primary group-hover:bg-primary/90 group-hover:scale-105 flex items-center justify-center shadow-lg transition-all">
            <Play className="w-6 h-6 text-white ml-0.5 fill-white" />
          </span>
        </span>
        {durationSec ? (
          <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/70 text-[10px] font-medium text-white">
            {fmt(durationSec)}
          </span>
        ) : null}
      </button>
    </div>
  );
}

export function VideoPlayerModal({
  title,
  videoUrl,
  id,
  meta,
  cleanUrl,
  burnedUrl,
  onRequestClean,
  poster,
  plan,
  orderId,
  videoProvider,
  fallbackFrom,
  approvedAt,
  durationSec,
  canRate = true,
  onClose,
}: {
  title: string;
  videoUrl: string;
  /** Order id — the share buttons link to the public /share/:id page (the Vivideo site),
   *  not the raw S3 file, so a shared link brings people in. Falls back to videoUrl if absent. */
  id?: string;
  meta: string;
  /** Captions toggle: the player swaps between the clean (no-captions) and the
   *  burned-in captioned renditions. When only one rendition exists there's no toggle. */
  cleanUrl?: string;
  burnedUrl?: string;
  /** LongVid: only the captioned file exists yet — the CC button has the clean one
   *  made on first press (see ResultVideoPlayer). Omitted for every other video. */
  onRequestClean?: () => Promise<string | null>;
  poster?: string;
  /** The video's plan card — fed to the AI caption composer so it can write a
   *  caption from the actual script/scenes, in the plan's language. */
  plan?: V2VideoPlan | null;
  /** Order the video belongs to — the like/dislike buttons need it, and it rides
   *  along on the download event. Callers without one simply show no buttons. */
  orderId?: string;
  /** The VIDEO's own provider (the order's) for the analytics props — never the
   *  user's current A/B arm. Absent on old orders that stored none: then nothing is sent. */
  videoProvider?: string | null;
  /** The order's fallback mark ('longvid_v1' | 'videogen_v1') when HeyGen made this
   *  video in that provider's place — rides on the same events as the provider. */
  fallbackFrom?: string | null;
  /** This video's order `approved_at`, when known — the events carry it as video_approved_ts / video_age_h. */
  approvedAt?: string | null;
  /** Delivered length in seconds, when the caller knows it. */
  durationSec?: number;
  /** False hides like/dislike (a video made before ratings existed). */
  canRate?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('v2');
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Captions state mirrored from the player so Download gives the matching file.
  const [captionsOn, setCaptionsOn] = useState(true);
  const playBase = cleanUrl || videoUrl;
  const downloadUrl = captionsOn ? (burnedUrl || videoUrl) : (cleanUrl || videoUrl);
  // Share the SITE link (/share/:id) — opening it lands friends on a branded Vivideo page
  // (where the video is embedded), not the raw S3 file. Falls back to the direct url if no id.
  const shareUrl = id ? `${window.location.origin}/share/${id}` : videoUrl;
  // Share intents (open the platform's composer with the share link).
  const shareText = t('videoPlayer.shareText', { title });
  const u = encodeURIComponent(shareUrl);
  const tt = encodeURIComponent(shareText);
  // `method` is the analytics share_method — one fixed word per target.
  const shareTargets = [
    { name: 'X', method: 'twitter', href: `https://twitter.com/intent/tweet?url=${u}&text=${tt}`, icon: Twitter, bg: 'bg-black' },
    { name: 'WhatsApp', method: 'whatsapp', href: `https://wa.me/?text=${tt}%20${u}`, icon: MessageCircle, bg: 'bg-[#25D366]' },
    { name: 'Telegram', method: 'telegram', href: `https://t.me/share/url?url=${u}&text=${tt}`, icon: Send, bg: 'bg-[#229ED9]' },
    { name: 'Facebook', method: 'facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${u}`, icon: Facebook, bg: 'bg-[#1877F2]' },
    { name: 'Email', method: 'email', label: t('videoPlayer.email'), href: `mailto:?subject=${tt}&body=${u}`, icon: Mail, bg: 'bg-surface-light' },
  ];

  // Every share action reports the same way; only share_method differs.
  const shareProps = (method: string) => ({
    share_method: method,
    order_id: orderId,
    provider: videoProvider || undefined,
    fallback_from: fallbackFrom || undefined,
    approved_at: approvedAt || undefined,
  });

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Like/dislike: one vote per delivered file, remembered in this browser, so the
  // buttons disappear on click and stay hidden when the video is reopened.
  const ratingKey = orderId ? videoRatingKey(orderId, videoUrl) : '';
  const [rated, setRated] = useState(() => (ratingKey ? getVideoRating(ratingKey) !== null : false));
  // The vote lands instantly; these two drive the short confirmation beat before the row goes.
  const [rateChoice, setRateChoice] = useState<'like' | 'dislike' | null>(null);
  const [rateExiting, setRateExiting] = useState(false);
  // A different video fed into the open modal is judged on its own key.
  useEffect(() => {
    setRated(ratingKey ? getVideoRating(ratingKey) !== null : false);
    setRateChoice(null);
    setRateExiting(false);
  }, [ratingKey]);
  // The chosen thumb holds lit for ~900ms, then the row collapses away and is removed.
  useEffect(() => {
    if (!rateChoice) return;
    const hold = setTimeout(() => setRateExiting(true), RATE_HOLD_MS);
    const gone = setTimeout(() => setRated(true), RATE_HOLD_MS + RATE_COLLAPSE_MS);
    return () => { clearTimeout(hold); clearTimeout(gone); };
  }, [rateChoice]);
  const handleRate = (rating: 'like' | 'dislike') => {
    if (!orderId || rateChoice) return;
    trackAmpVideoRated({ rating, orderId, provider: videoProvider, fallbackFrom, approvedAt, videoDurationS: durationSec });
    setVideoRating(ratingKey, rating);
    setRateChoice(rating);
  };

  const handleCopy = async () => {
    trackVideoShared('video', shareProps('copy_link'));
    try { await navigator.clipboard.writeText(shareUrl); } catch { /* best effort */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Force a real save-to-disk — never a new tab. The plain `download` attribute is IGNORED
  // for cross-origin files (our S3), so the browser would just open/play the video. Primary
  // path: fetch the bytes into a blob and save THAT (works today thanks to our open S3 CORS).
  // Fallback (e.g. a very large video that won't fit in memory, or a CORS hiccup): the backend
  // `v2-share?action=download` endpoint 302s to a presigned url carrying
  // `Content-Disposition: attachment`, so it still saves directly instead of opening a tab.
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    const safe = (title || 'vivideo').replace(/[^\w\-]+/g, '_').slice(0, 60) || 'vivideo';
    trackVideoDownloaded('video', {
      order_id: orderId,
      provider: videoProvider || undefined,
      fallback_from: fallbackFrom || undefined,
      approved_at: approvedAt || undefined,
      captions: captionsOn ? 'on' : 'off',
    });
    setDownloading(true);
    try {
      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error(`http ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${safe}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
    } catch {
      const endpoint =
        `${API_CONFIG.baseUrl}/v2-share?action=download` +
        `&url=${encodeURIComponent(downloadUrl)}` +
        `&name=${encodeURIComponent(safe)}&cc=${captionsOn ? 1 : 0}`;
      const a = document.createElement('a');
      a.href = endpoint;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setDownloading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-7xl max-h-[94vh] overflow-hidden rounded-2xl bg-surface border border-glass-border shadow-2xl flex flex-col lg:flex-row animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-surface/80 border border-glass-border flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors"
          aria-label={t('videoPlayer.close')}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Player — branded controls (play/pause, scrub, volume, captions, fullscreen) */}
        <div className="flex-1 min-w-0 bg-black flex items-center">
          <div className="w-full aspect-video max-h-[86vh]">
            <ResultVideoPlayer
              src={playBase}
              poster={poster}
              autoPlay
              burnedSrc={burnedUrl}
              onCaptionsChange={setCaptionsOn}
              onRequestClean={onRequestClean}
            />
          </div>
        </div>

        {/* Right: download + share */}
        <aside className="w-full lg:w-[360px] shrink-0 border-t lg:border-t-0 lg:border-l border-glass-border p-6 overflow-y-auto">
          <h2 className="text-lg font-bold text-text-primary mb-1 leading-snug">{title}</h2>
          <p className="text-xs text-text-tertiary mb-6">{meta}</p>

          <button
            onClick={() => void handleDownload()}
            disabled={downloading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-bold hover:opacity-95 hover:shadow-glow transition-all disabled:opacity-60"
          >
            {downloading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            {downloading ? t('videoPlayer.preparing') : t('videoPlayer.downloadVideo')}
          </button>
          {burnedUrl && cleanUrl && burnedUrl !== cleanUrl && (
            <p className="text-[11px] text-text-tertiary text-center mt-2">
              {captionsOn ? t('videoPlayer.downloadHintCaptioned') : t('videoPlayer.downloadHintNoCaptions')}
            </p>
          )}

          {/* Publish to already-connected social accounts — its own "Connected
              Accounts N/6" bar (post-only; connecting happens on Integrations). */}
          <div className="mt-4">
            <SocialPublish videoUrl={downloadUrl} title={title} plan={plan} orderId={orderId} videoProvider={videoProvider} fallbackFrom={fallbackFrom} approvedAt={approvedAt} />
          </div>

          <button
            onClick={() => setShareOpen((v) => !v)}
            className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-surface-light border border-glass-border text-sm font-bold text-text-primary hover:border-primary/40 transition-colors"
          >
            <Share2 className="w-5 h-5 text-text-secondary" />
            {t('videoPlayer.share')}
            <ArrowDown className={cn('w-4 h-4 text-text-tertiary transition-transform', shareOpen && 'rotate-180')} />
          </button>

          {orderId && canRate && !rated && (
            <div
              data-testid="video-rating-row"
              className={cn(
                'overflow-hidden transition-all ease-out motion-reduce:transition-none',
                rateExiting ? 'mt-0 max-h-0 opacity-0' : 'mt-3 max-h-24 opacity-100'
              )}
              style={{ transitionDuration: `${RATE_COLLAPSE_MS}ms` }}
            >
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleRate('like')}
                  disabled={rateChoice !== null}
                  aria-pressed={rateChoice === 'like'}
                  aria-label={t('videoPlayer.like')}
                  title={t('videoPlayer.like')}
                  className={cn(
                    'flex-1 flex items-center justify-center px-4 py-3 rounded-xl border transition-all duration-200',
                    rateChoice === 'like'
                      ? 'bg-primary/15 border-primary text-primary motion-safe:animate-rate-pop'
                      : 'bg-surface-light border-glass-border text-text-secondary hover:text-text-primary hover:border-primary/40',
                    rateChoice === 'dislike' && 'opacity-0'
                  )}
                >
                  <ThumbsUp className="w-5 h-5" fill={rateChoice === 'like' ? 'currentColor' : 'none'} />
                </button>
                <button
                  onClick={() => handleRate('dislike')}
                  disabled={rateChoice !== null}
                  aria-pressed={rateChoice === 'dislike'}
                  aria-label={t('videoPlayer.dislike')}
                  title={t('videoPlayer.dislike')}
                  className={cn(
                    'flex-1 flex items-center justify-center px-4 py-3 rounded-xl border transition-all duration-200',
                    rateChoice === 'dislike'
                      ? 'bg-primary/15 border-primary text-primary motion-safe:animate-rate-pop'
                      : 'bg-surface-light border-glass-border text-text-secondary hover:text-text-primary hover:border-primary/40',
                    rateChoice === 'like' && 'opacity-0'
                  )}
                >
                  <ThumbsDown className="w-5 h-5" fill={rateChoice === 'dislike' ? 'currentColor' : 'none'} />
                </button>
              </div>
            </div>
          )}

          {shareOpen && (
            <div className="mt-2 space-y-2 animate-fade-in">
              <button
                onClick={handleCopy}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-primary/30 bg-primary/10 hover:bg-primary/15 transition-colors"
              >
                <span className="w-8 h-8 rounded-lg bg-surface flex items-center justify-center">
                  {copied ? <Check className="w-4 h-4 text-primary" /> : <Link2 className="w-4 h-4 text-primary" />}
                </span>
                <span className="flex-1 text-left text-sm font-semibold text-text-primary">
                  {copied ? t('videoPlayer.linkCopied') : t('videoPlayer.copyVideoLink')}
                </span>
              </button>
              {shareTargets.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackVideoShared('video', shareProps(s.method))}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-glass-border bg-surface-light hover:border-primary/30 transition-colors"
                >
                  <span className={cn('w-8 h-8 rounded-lg flex items-center justify-center', s.bg)}>
                    <s.icon className="w-4 h-4 text-white" />
                  </span>
                  <span className="flex-1 text-left text-sm font-medium text-text-primary">{('label' in s && s.label) || s.name}</span>
                </a>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>,
    document.body
  );
}
